import { Centrifuge, State, type Subscription, type PublicationContext } from 'centrifuge';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type EventHandler = (event: { type: string; data: unknown }) => void;

export const centrifugoContext = createContext<Centrifuge | null>(null);

export function createCentrifugoClient(endpoint: string): Centrifuge {
  return new Centrifuge(endpoint, {
    debug: false,
  });
}

let singleton: { endpoint: string; client: Centrifuge } | null = null;

export function getCentrifugoClient(endpoint: string): Centrifuge {
  if (singleton?.endpoint === endpoint) return singleton.client;
  if (singleton) singleton.client.disconnect();
  singleton = { endpoint, client: createCentrifugoClient(endpoint) };
  return singleton.client;
}

type SubscriptionEntry = {
  subscription: Subscription;
  listeners: Set<EventHandler>;
};

const subscriptionRegistry = new Map<string, SubscriptionEntry>();

function getOrCreateSubscription(client: Centrifuge, channel: string): SubscriptionEntry {
  const existing = subscriptionRegistry.get(channel);
  if (existing) return existing;

  const subscription = client.newSubscription(channel);

  subscription.on('publication', (ctx: PublicationContext) => {
    const entry = subscriptionRegistry.get(channel);
    if (!entry) return;
    const event = ctx.data as { type: string; data: unknown };
    for (const handler of entry.listeners) {
      handler(event);
    }
  });

  subscription.subscribe();

  const entry: SubscriptionEntry = { subscription, listeners: new Set() };
  subscriptionRegistry.set(channel, entry);
  return entry;
}

function removeListener(client: Centrifuge, channel: string, handler: EventHandler): void {
  const entry = subscriptionRegistry.get(channel);
  if (!entry) return;

  entry.listeners.delete(handler);

  if (entry.listeners.size === 0) {
    entry.subscription.unsubscribe();
    client.removeSubscription(entry.subscription);
    subscriptionRegistry.delete(channel);
  }
}

export function useCentrifugo(): Centrifuge | null {
  return useContext(centrifugoContext);
}

export function useSubscription(channel: string | null, handler: EventHandler): void {
  const client = useContext(centrifugoContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const stableHandler = useCallback((event: { type: string; data: unknown }) => {
    handlerRef.current(event);
  }, []);

  useEffect(() => {
    if (!client || !channel) return;

    const entry = getOrCreateSubscription(client, channel);
    entry.listeners.add(stableHandler);

    return () => {
      removeListener(client, channel, stableHandler);
    };
  }, [client, channel, stableHandler]);
}

export function useStreamerEvents(streamerId: number | null, handler: EventHandler): void {
  const channel = streamerId != null ? `streamer:${streamerId}` : null;
  useSubscription(channel, handler);
}

export function useStreamerAdminEvents(streamerId: number | null, handler: EventHandler): void {
  const channel = streamerId != null ? `streamer-admin:${streamerId}` : null;
  useSubscription(channel, handler);
}

export function useStreamerCategoryEvents(
  streamerId: number | null,
  category: string,
  handler: EventHandler,
): void {
  const channel = streamerId != null ? `streamer:${streamerId}-${category}` : null;
  useSubscription(channel, handler);
}

export function useStreamerAdminCategoryEvents(
  streamerId: number | null,
  category: string,
  handler: EventHandler,
): void {
  const channel = streamerId != null ? `streamer-admin:${streamerId}-${category}` : null;
  useSubscription(channel, handler);
}

export function useUserCategoryEvents(
  userId: number | null,
  category: string,
  handler: EventHandler,
): void {
  const channel = userId != null ? `user:${userId}-${category}` : null;
  useSubscription(channel, handler);
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

function stateToStatus(state: State): ConnectionStatus {
  switch (state) {
    case State.Connected:
      return 'connected';
    case State.Connecting:
      return 'connecting';
    default:
      return 'disconnected';
  }
}

export function useConnectionStatus(): ConnectionStatus {
  const client = useContext(centrifugoContext);
  const [status, setStatus] = useState<ConnectionStatus>(
    client ? stateToStatus(client.state) : 'disconnected',
  );

  useEffect(() => {
    if (!client) {
      setStatus('disconnected');
      return;
    }

    setStatus(stateToStatus(client.state));

    const onState = (ctx: { newState: State }) => {
      setStatus(stateToStatus(ctx.newState));
    };

    client.on('state', onState);
    return () => {
      client.off('state', onState);
    };
  }, [client]);

  return status;
}

export function useRevalidateOnReconnect(revalidate: () => void): void {
  const client = useContext(centrifugoContext);
  const wasDisconnectedRef = useRef(false);

  useEffect(() => {
    if (!client) return;

    const onDisconnected = () => {
      wasDisconnectedRef.current = true;
    };

    const onConnected = () => {
      if (wasDisconnectedRef.current) {
        wasDisconnectedRef.current = false;
        revalidate();
      }
    };

    client.on('disconnected', onDisconnected);
    client.on('connected', onConnected);
    return () => {
      client.off('disconnected', onDisconnected);
      client.off('connected', onConnected);
    };
  }, [client, revalidate]);
}

export function resetSubscriptionRegistry(): void {
  subscriptionRegistry.clear();
}
